import { z } from "zod";
import { QueryOptions, queryOptions } from "@tanstack/react-query";

import {
  Plan,
  CreditLog,
  PlanSchema,
  CreditLogSchema,
  SubscriptionSchema,
  AddonPaymentAutocharge,
  AddonPaymentAutochargeSchema,
  AddonPaymentManualCharge,
  AddonPaymentManualChargeSchema,
  OrderFulfillment,
  OrderFulfillmentSchema,
} from "@/schema/subscription";

import { api, ApiOptions, mBaseURL } from "@/config/api";
import { APIResponseSuccessSchema } from "@/schema/api";
import { unwrapApiError } from "@/lib/error";
import { createURLPath } from "@/lib/utils";

export interface CreateAddonPaymentProps {
  quantity: number;
  callback_url: string;
}

export interface CreateSubscriptionProps {
  email: string;
  plan_id: string;
  callback_url: string;
  failure_callback_url?: string;
  with_trial?: boolean;
  ui_mode?: "hosted" | "embedded" | "custom";
}

interface FetchCreditLogsProps {
  start_date?: string;
  end_date?: string;
}

interface RetentionRequest {
  user_id: string;
}

interface CancelSubscriptionRequest {
  user_id: string;
  comment?: string;
  reasons?: string;
  cancel_at_period_end: boolean;
}

export const SubscriptionApiFactory = {
  Endpoint: mBaseURL + "/api/subscriptions",
  Keys: {
    FetchAllPlans: () => ["subscription", "plans"],
    FetchCreditLogs: () => ["subscription", "credits", "logs"],
    FetchOrderFulfillment: (session_id: string) => ["subscription", "order", "fulfillment", session_id],
  },
  Schemas: {
    Response: {
      FetchUserSubscription: APIResponseSuccessSchema.extend({
        data: SubscriptionSchema,
      }),
      FetchAllPlans: APIResponseSuccessSchema.extend({
        data: PlanSchema.array(),
      }),
      CreateAddonAutochargePayment: APIResponseSuccessSchema.extend({
        data: AddonPaymentAutochargeSchema.omit({
          success: true,
        }),
      }),
      CreateAddonManualChargePayment: APIResponseSuccessSchema.extend({
        data: AddonPaymentManualChargeSchema.omit({
          success: true,
        }),
      }),
      CreateCustomerPortalSession: APIResponseSuccessSchema.extend({
        data: z.object({
          data: z.string().url(),
        }),
      }),
      CreateSubscription: APIResponseSuccessSchema.extend({
        data: z.object({
          data: z.object({
            client_secret: z.string(),
            checkout_session_id: z.string(),
            redirect_url: z.string().url().nullish(),
          }),
        }),
      }),
      OrderFulfillment: APIResponseSuccessSchema.extend({
        data: OrderFulfillmentSchema,
      }),
      CheckValidSubscription: APIResponseSuccessSchema.extend({
        data: z.boolean(),
      }),
      FetchCreditLogs: APIResponseSuccessSchema.extend({
        data: z.array(CreditLogSchema),
      }),
      RetentionCredits: APIResponseSuccessSchema.extend({
        data: z.object({
          credits: z.number(),
        }),
      }),
      RetentionCoupon: APIResponseSuccessSchema.extend({
        data: z.object({
          status: z.boolean(),
          message: z.string(),
          request: z.any(),
        }),
      }),
      CancelSubscriptionRequest: APIResponseSuccessSchema.extend({
        data: z.object({
          status: z.boolean(),
          message: z.string(),
          request: z.any(),
        }),
      }),
    },
  },
  Apis: {
    FetchAllPlans: async (options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "plans"), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.FetchAllPlans.parse)
        .then((data) => data.data);
    },
    FetchUserSubscription: async (options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "me"), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(SubscriptionApiFactory.Schemas.Response.FetchUserSubscription.parse);
    },

    CreateAddonPayment: async (body: CreateAddonPaymentProps, options?: ApiOptions): Promise<AddonPaymentAutocharge | AddonPaymentManualCharge> => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "create/addon"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        try {
          const json = await response.json();
          const parsed = SubscriptionApiFactory.Schemas.Response.CreateAddonManualChargePayment.safeParse(json);

          if (parsed.success) {
            return Object.assign(parsed.data.data, { success: false as const });
          } else {
            throw new Error("Oops, something went wrong! Please try again after some time. Please get in touch with support if you card has been charged but credits were not added.");
          }
        } catch {
          throw new Error("Oops, something went wrong! Please try again after some time. Please get in touch with support if you card has been charged but credits were not added.");
        }
      }
      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.CreateAddonAutochargePayment.parse)
        .then((response) => Object.assign(response.data, { success: true as const }));
    },
    CreateCustomerPortalSession: async (callback_url: string, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "create", "customer-portal-session"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ callback_url }),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(SubscriptionApiFactory.Schemas.Response.CreateCustomerPortalSession.parse);
    },

    CreateSubscription: async (body: CreateSubscriptionProps, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "create"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.CreateSubscription.parse)
        .then((response) => response.data.data);
    },
    FetchOrderFulfillment: async (session_id: string, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, `order/fulfillment?session_id=${encodeURIComponent(session_id)}`), {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Failed to retrieve order fulfillment" }));
        throw error;
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.OrderFulfillment.parse)
        .then((response) => response.data);
    },

    UpdateSubscription: async (plan_id: string, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "update"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id }),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json();
    },

    CheckSubscription: async (email: string, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "check?email=" + encodeURIComponent(email)), {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(SubscriptionApiFactory.Schemas.Response.CheckValidSubscription.parse);
    },
    FetchCreditLogs: async (body: FetchCreditLogsProps, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "credits", "logs"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.FetchCreditLogs.parse)
        .then((response) => response.data);
    },
    AddRetentionCredits: async (body: RetentionRequest, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "retention", "credits"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.RetentionCredits.parse)
        .then((response) => response.data);
    },
    ApplyRetentionCoupon: async (body: RetentionRequest, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "retention", "coupon"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.RetentionCoupon.parse)
        .then((response) => response.data);
    },
    CancelSubscription: async (body: CancelSubscriptionRequest, options?: ApiOptions) => {
      const response = await api(createURLPath(SubscriptionApiFactory.Endpoint, "cancel"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(SubscriptionApiFactory.Schemas.Response.CancelSubscriptionRequest.parse)
        .then((response) => response.data);
    },
  },
  Queries: {
    FetchAllPlans: (options?: QueryOptions<Plan[]>) => {
      return queryOptions({
        queryKey: SubscriptionApiFactory.Keys.FetchAllPlans(),
        queryFn: () => SubscriptionApiFactory.Apis.FetchAllPlans(),
        select: (data) => data.filter((plan) => plan.id === "starter" || plan.id === "pro").toSorted((a, b) => (a.price || 0) - (b.price || 0)),
        ...options,
      });
    },
    FetchCreditLogs: (props: FetchCreditLogsProps, options?: QueryOptions<CreditLog[]>) => {
      return queryOptions({
        queryKey: SubscriptionApiFactory.Keys.FetchCreditLogs(),
        queryFn: () => SubscriptionApiFactory.Apis.FetchCreditLogs(props),
        ...options,
      });
    },
    FetchOrderFulfillment: (session_id: string, options?: QueryOptions<OrderFulfillment>) => {
      return queryOptions({
        queryKey: SubscriptionApiFactory.Keys.FetchOrderFulfillment(session_id),
        queryFn: () => SubscriptionApiFactory.Apis.FetchOrderFulfillment(session_id),
        ...options,
      });
    },
  },
};
